import {
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Theme,
	Typography,
	makeStyles,
} from "@material-ui/core";
import PlaylistAddIcon from "@material-ui/icons/PlaylistAdd";
import {
	ColDef,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.min.css";
import SearchIcon from "@material-ui/icons/Search";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { useContextMenuMESF } from "../../../../../controls";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { Job } from "../../models/Job";
import { ResetJob } from "../ResetJob";
import { OnEnableJob } from "../onEnableJob";
import { JobDetails } from "./JobsDetails";
import { useJobsOptionsFunctions } from "./customHooks/useJobsOptionsFunctions";
import { useJobsTableData } from "./customHooks/useJobsTableData";

const CustomStatusPanel = ({ handleNew }: { handleNew: () => void }) => (
	<IconButton onClick={handleNew} color="primary" style={{ marginLeft: -15 }}>
		<PlaylistAddIcon style={{ width: 30, height: 30 }} />
	</IconButton>
);

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		"& .MuiDataGrid-row": {
			cursor: "pointer",
		},
		"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
			fontWeight: "bold",
		},
		"& .ag-icon-menu": {
			display: "none",
		},
		"& .ag-header-cell-label": {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontSize: "0.85rem",
		},

		"& .ag-header-cell-text": {
			textAlign: "center !important",
		},

		"& .ag-checkbox-input-wrapper": {
			margin: "0 10px",
		},
		"& .ag-cell": {
			alignItems: "center",
			padding: "0 !important",
			border: "unset !important",
		},
		"& .ag-header-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		"& .ag-cell-wrapper": {
			textAlign: "center",
		},
		"& .ag-menu-option": {
			cursor: "pointer",
		},
	},
	card: {
		backgroundColor: "#e9f2f8",
	},
	filters: {
		position: "relative",
		margin: "0 10px",
	},
	textfield: {
		margin: theme.spacing(2),
	},
	JobsTable: {
		padding: theme.spacing(1, 2),
	},
	form: {
		display: "flex",
		flexDirection: "column",
	},
	table: {
		"& .ag-header-cell": {
			padding: "2px !important",
			fontSize: 11,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		"& .ag-header-cell-label": {
			justifyContent: "center",
		},
	},
}));

type Props = {
	getJobsFromAPI: Function;
	isLoading: boolean;
};

export const TableJobs = ({ getJobsFromAPI, isLoading }: Props) => {
	const classes = useStyles();

	const [jobSelected, setJobSelected] = useState<Job | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [showModal, setShowModal] = useState<
		"create" | "update" | "reset" | "enable" | ""
	>("");

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			autoHeaderHeight: true,
		};
	}, []);

	const onRowSelected = (event: RowSelectedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			setJobSelected(event.data);
			setShowModal("update");
		}
		if (cell?.column.getColId() === "Enabled") {
			setJobSelected(event.data);
			setShowModal("enable");
		}
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { getMenuOptions } = useJobsOptionsFunctions({
		setShowModal,
		setJobSelected,
		getJobsFromAPI,
	});
	const { rows, columnDefs } = useJobsTableData({
		setJobSelected,
		setShowModal,
		showContextMenu,
	});

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		showContextMenu(event as any, data, "jobContext");
		if (data) {
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	const handleNewJob = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setJobSelected(null);
	};

	const onDetailsHide = async ({
		close,
		entityId,
	}: {
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setJobSelected(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		await getJobsFromAPI().then(() => {
			if (entityId) {
				setTimeout(() => {
					const rowNode = gridApi?.getRowNode(String(entityId));

					if (rowNode) {
						gridApi?.ensureIndexVisible(rowNode?.rowIndex ?? 0);
						rowNode?.setSelected(true);
						setJobSelected(rowNode?.data);
					}
				}, 0);
			}
		});
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "jobsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid container justifyContent="center">
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px", margin: "10px 0px" }}
				>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						System Jobs
					</Typography>
				</Grid>
				<Grid
					container
					style={{ width: "100%", padding: "0 10px" }}
					spacing={2}
				>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							label="Search"
							variant="outlined"
							size="small"
							margin="dense"
							value={filterValue}
							onChange={(event) => {
								setFilterValue(event.target.value);
								gridApi?.setQuickFilter(event.target.value);
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<IconButton edge="start" type="submit">
											<SearchIcon />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				</Grid>
				<Grid
					container
					justifyContent="flex-start"
					alignItems="center"
					className={classes.root}
					style={{ width: "100%", padding: "0 10px" }}
					spacing={2}
				>
					<Grid item md={6} xs={12}>
						<Paper
							elevation={2}
							style={{ height: "70vh", width: "100%" }}
							className={`${classes.table} ag-theme-balham`}
						>
							<AgGridReact
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								getContextMenuItems={getContextMenuItems}
								loadingOverlayComponent={LazyLoading}
								suppressContextMenu={false}
								rowHeight={34}
								animateRows
								pagination={false}
								rowSelection="single"
								onGridReady={(params) => setGridApi(params.api)}
								getRowId={(params) => `${(params.data as Job).JobId}`}
								onRowSelected={onRowSelected}
								gridOptions={{
									statusBar: {
										statusPanels: [
											{
												statusPanel: "agTotalRowCountComponent",
												align: "right",
											},
											{
												statusPanel: (params: IStatusPanelParams) => (
													<CustomStatusPanel handleNew={handleNewJob} />
												),
												align: "left",
											},
										],
									},
								}}
							/>
						</Paper>
					</Grid>
					<Grid item md={6} xs={12}>
						<Paper
							elevation={2}
							style={{ height: "70vh", width: "100%", padding: 15 }}
							className={`${classes.table} ag-theme-balham`}
						>
							<JobDetails
								job={jobSelected}
								modal={showModal}
								onHide={onDetailsHide}
							/>
						</Paper>
					</Grid>
				</Grid>
			</Grid>
			<ResetJob
				show={showModal === "reset"}
				onHide={async (shouldUpdate: boolean) => {
					setShowModal("");
					setJobSelected(null);
					if (gridApi) {
						gridApi.deselectAll();
					}
					if (shouldUpdate) {
						await getJobsFromAPI();
					}
				}}
				jobSelected={jobSelected}
			/>
			<OnEnableJob
				show={showModal === "enable"}
				onHide={async (shouldUpdate: boolean) => {
					setShowModal("");
					setJobSelected(null);
					if (gridApi) {
						gridApi.deselectAll();
					}
					if (shouldUpdate) {
						await getJobsFromAPI();
					}
				}}
				jobSelected={jobSelected}
			/>
			<ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
		</>
	);
};
